import React, { useState, useRef, useEffect } from 'react';
import { useSpring, animated } from '@react-spring/web';
import './VoiceRecordingFlow.css';
import { sanitizeInput } from '../utils/security';
import { auth, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, listAll } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { getUserFolderPath } from '../utils/firebaseUtils';
import TorusAnimation from './TorusAnimation';
import { requestMicrophone, defaultAudioConstraints, getFriendlyMicError } from '../utils/media';
import aiService from '../services/AIService';
import { analyzeAudioLocally } from '../utils/localAudioAnalysis';
import { useSpacebarRecording } from '../hooks/useSpacebarRecording';

// Firebase Storage uploads disabled — audio files not needed, avoids RGPD risk on demo
const FIREBASE_UPLOAD_ENABLED = false;

// Configuration THOR 42 - 3 phases dans les 42 secondes
const THOR42_PHASES = [
  { start: 0, end: 18, instruction: 'Parlez librement de votre journée' },
  { start: 18, end: 32, instruction: 'Chantez : Ah-Eh-Ih-Oh-Ou en montant' },
  { start: 32, end: 42, instruction: 'Respirez en vocalisant Ahhhhhh' }
];

const STEPS = [
  {
    id: 'thor42',
    title: 'Diagnostic vocal',
    instruction: 'Diagnostic vocal en 42 secondes - Parlez librement de votre journée',
    duration: 42, // secondes exactement
    audioFile: () => `thor42-diagnostic-${Date.now()}.wav`
  }
];

// Fonction pour obtenir l'instruction selon le temps écoulé
const getThor42Instruction = (timeRemaining) => {
  const elapsed = 42 - timeRemaining;
  const phase = THOR42_PHASES.find(p => elapsed >= p.start && elapsed < p.end);
  return phase ? phase.instruction : STEPS[0].instruction;
};

const VoiceRecordingFlow = ({ user, onClose, onRecordingStateChange, onCountdownChange, onLiveFrequencyChange, onDiagnosticProgress, onDiagnosticComplete, onDiagnosticWarningChange, onDiagnosticErrorChange, onDiagnosticLoadingChange, onAnalyzingBetweenStepsChange, onStartRecording, onStopRecording, onOpenProfile, startAtStep = null }) => {
  // VoiceRecordingFlow utilise UNIQUEMENT le mode minimal - les autres modes sont désactivés
  const isMinimal = true;   // Mode minimal obligatoire pour diagnostic THOR 42
  const isOverlay = false;  // Jamais utilisé pour le diagnostic principal
  const isComplete = false; // Mode non utilisé
  
  const [currentStep, setCurrentStep] = useState(0);
  
  // Fonction de sécurité pour éviter l'erreur STEPS[currentStep] undefined
  const getCurrentStepSafely = () => {
    return currentStep < STEPS.length ? STEPS[currentStep] : STEPS[0];
  };
  const [currentInstruction, setCurrentInstruction] = useState(STEPS[0].instruction);
  const [isRecording, setIsRecording] = useState(false);
  const [recordings, setRecordings] = useState({});
  const [isPaused, setIsPaused] = useState(false);
  const [showConfirmExit, setShowConfirmExit] = useState(false);
  const [showAlreadySubmittedWarning, setShowAlreadySubmittedWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [hasAlreadySubmitted, setHasAlreadySubmitted] = useState(false);
  const [finalAnalyzing, setFinalAnalyzing] = useState(false);
  const [finalDiagnostic, setFinalDiagnostic] = useState(null);
  const [showFinalDiagnostic, setShowFinalDiagnostic] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dailyUploads, setDailyUploads] = useState(0);
  // État userInfoStep supprimé - plus besoin de cette étape
  const [liveFrequency, setLiveFrequency] = useState(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const animationFrameRef = useRef(null);
  const frequencyHistoryRef = useRef([]);
  const smoothedFrequencyRef = useRef(0);
  
  // Nouveaux états pour timer et analyse
  const [countdown, setCountdown] = useState(0);
  const [stepAnalysis, setStepAnalysis] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isAnalyzingBetweenSteps, setIsAnalyzingBetweenSteps] = useState(false);
  const timerRef = useRef(null);

  const spring = useSpring({
    opacity: 1,
    transform: 'translateY(0)',
    from: { opacity: 0, transform: 'translateY(20px)' }
  });

  // ✨ useEffect pour fermer automatiquement après diagnostic (DOIT être au top level)
  useEffect(() => {
    if (isMinimal && showFinalDiagnostic && finalDiagnostic) {
      const timer = setTimeout(() => {
        onClose();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isMinimal, showFinalDiagnostic, finalDiagnostic, onClose]);

  // Charger la progression sauvegardée au démarrage
  useEffect(() => {
    const progressKey = `diagnostic_progress_${user.uid}`;
    const savedProgress = localStorage.getItem(progressKey);
    if (savedProgress) {
      try {
        const { currentStep: savedStep, recordings: savedRecordings } = JSON.parse(savedProgress);
        
        // Valider savedStep pour éviter l'erreur STEPS[currentStep] undefined
        const validatedStep = savedStep >= 0 && savedStep < STEPS.length ? savedStep : 0;
        if (validatedStep !== savedStep) {
          console.warn(`⚠️ currentStep corrigé de ${savedStep} à ${validatedStep}`);
        }
        
        // Convertir automatiquement les anciens formats au lieu de les supprimer
        console.log('📂 Chargement des enregistrements sauvegardés...');
        
        // Restaurer les enregistrements depuis base64 URLs
        const restoredRecordings = {};
        Object.entries(savedRecordings).forEach(([stepId, recordData]) => {
          console.log(`🔄 Restoring ${stepId}:`, {
            type: typeof recordData,
            hasUrl: !!(recordData?.url),
            isBase64: !!(recordData?.isBase64),
            data: recordData
          });
          
          if (typeof recordData === 'object' && recordData.isBase64) {
            restoredRecordings[stepId] = {
              url: recordData.url,           // Base64 data URL (persistent)
              timestamp: recordData.timestamp,
              size: recordData.size,
              isBase64: true,
              blob: null                     // Will be recreated when needed
            };
          } else if (typeof recordData === 'string' && recordData.startsWith('data:')) {
            // Legacy base64 string format
            restoredRecordings[stepId] = {
              url: recordData,               // Base64 data URL
              timestamp: Date.now(),
              size: 0,
              isBase64: true,
              blob: null
            };
          } else {
            // Very old format or invalid - skip to force re-recording
            console.warn(`⚠️ Skipping invalid recording format for ${stepId}:`, recordData);
          }
        });
        
        // Defer state updates to avoid render cycle conflicts
        setTimeout(() => {
          setCurrentStep(validatedStep);
          setRecordings(restoredRecordings);
        }, 0);
      } catch (error) {
        console.error('❌ Erreur lors du chargement de la progression:', error);
        localStorage.removeItem(progressKey);
      }
    }
  }, [user.uid]);

  // Sauvegarder la progression à chaque changement (base64 URLs sont persistantes)
  useEffect(() => {
      // Sauver les URLs base64 (persistantes) sans les blobs (trop volumineux)
      const recordingsForStorage = {};
      Object.entries(recordings).forEach(([stepId, recordingData]) => {
        if (typeof recordingData === 'object' && recordingData.url && recordingData.isBase64) {
          recordingsForStorage[stepId] = {
            url: recordingData.url,         // Base64 data URL
            timestamp: recordingData.timestamp,
            size: recordingData.size,
            isBase64: true
          };
        } else if (typeof recordingData === 'string') {
          recordingsForStorage[stepId] = recordingData; // String directe
        }
      });
      
      const progress = {
        currentStep,
        recordings: recordingsForStorage
      };
      localStorage.setItem(`diagnostic_progress_${user.uid}`, JSON.stringify(progress));
  }, [currentStep, recordings, user.uid]);


  // Vérifier si l'utilisateur a déjà soumis ses enregistrements
  useEffect(() => {
    const checkPreviousSubmission = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const userFolder = getUserFolderPath(user);
        const storageRef = ref(storage, userFolder);
        const listResult = await listAll(storageRef);
        if (listResult.items.length > 0) {
          setHasAlreadySubmitted(true);
          setShowAlreadySubmittedWarning(true);
          // Notifier App.jsx de l'état du warning
          if (onDiagnosticWarningChange) {
            onDiagnosticWarningChange(true);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la vérification des soumissions précédentes:", error);
      }
    };
    checkPreviousSubmission();
  }, []);

  // Exposer la fonction de restart globalement pour App.jsx
  useEffect(() => {
    const restart = () => {
      setShowAlreadySubmittedWarning(false);
      setCurrentStep(0);
      setRecordings({});
      setIsRecording(false);
      setIsPaused(false);
      setIsSubmitting(false);
      setShowSuccess(false);
      setShowRecap(false);
      setError('');
      if (onDiagnosticWarningChange) {
        onDiagnosticWarningChange(false);
      }
    };
    
    window.restartDiagnostic = restart;
    return () => {
      delete window.restartDiagnostic;
    };
  }, [onDiagnosticWarningChange]);


  // Handle direct navigation from dashboard
  useEffect(() => {
    if (startAtStep !== null && startAtStep >= 0 && startAtStep < STEPS.length) {
      console.log(`🚀 Starting diagnostic at step ${startAtStep + 1}`);
      
      // 🔒 VÉRIFICATION E1 OBLIGATOIRE
      // Vérifier si E1 (identification) a déjà été fait
      const hasE1 = user && localStorage.getItem(`diagnostic_complete_${user.uid}`) && 
                    JSON.parse(localStorage.getItem(`diagnostic_complete_${user.uid}`) || '[]')[0];
      
      if (!hasE1 && startAtStep !== 0) {
        console.log('🚫 E1 obligatoire - Redirection vers Identification');
        setMustStartWithE1(true);
        setCurrentStep(0); // Forcer E1
        
        // Notifier l'erreur pour affichage UX
        if (onDiagnosticErrorChange) {
          onDiagnosticErrorChange('⚠️ Commencez par l\'étape 1 : Identification');
        }
      } else {
        console.log('✅ E1 déjà fait ou étape 1 sélectionnée');
        setMustStartWithE1(false);
        setCurrentStep(startAtStep);
      }
    }
  }, [startAtStep, user]);

  // 📊 INITIALISER LE COMPTEUR QUOTIDIEN d'uploads Firebase
  useEffect(() => {
    if (user?.uid) {
      const today = new Date().toISOString().split('T')[0];
      const dailyCountKey = `firebase_daily_${user.uid}_${today}`;
      const currentCount = parseInt(localStorage.getItem(dailyCountKey) || '0');
      setDailyUploads(currentCount);
      console.log(`📊 Firebase uploads aujourd'hui: ${currentCount}/20`);
    }
  }, [user?.uid]);

  const startLiveAnalysis = () => {
    if (!analyserRef.current) return;
    
    const analyze = () => {
      if (!analyserRef.current) return;
      
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Trouver la fréquence dominante
      let maxValue = 0;
      let maxIndex = 0;
      
      // Ignorer les très basses fréquences (bruit)
      for (let i = 10; i < bufferLength / 2; i++) {
        if (dataArray[i] > maxValue) {
          maxValue = dataArray[i];
          maxIndex = i;
        }
      }
      
      // Calculer la fréquence seulement si l'amplitude est suffisante
      if (maxValue > 30) { // Seuil de bruit
        const frequency = maxIndex * audioContextRef.current.sampleRate / (bufferLength * 2);
        
        // Limiter la fréquence à des valeurs réalistes pour la voix humaine
        const clampedFrequency = Math.max(80, Math.min(frequency, 2000));
        
        // Ajouter à l'historique (garder les 10 dernières valeurs)
        frequencyHistoryRef.current.push(clampedFrequency);
        if (frequencyHistoryRef.current.length > 10) {
          frequencyHistoryRef.current.shift();
        }
        
        // Calculer la moyenne pondérée (plus de poids aux valeurs récentes)
        const weights = [0.05, 0.05, 0.05, 0.05, 0.1, 0.1, 0.15, 0.15, 0.15, 0.15];
        let weightedSum = 0;
        let totalWeight = 0;
        
        frequencyHistoryRef.current.forEach((freq, index) => {
          const weight = weights[index] || 0.1;
          weightedSum += freq * weight;
          totalWeight += weight;
        });
        
        const averageFrequency = weightedSum / totalWeight;
        
        // Lissage exponentiel pour éviter les changements brusques
        const smoothingFactor = 0.2; // Plus petit = plus lisse
        smoothedFrequencyRef.current = smoothedFrequencyRef.current * (1 - smoothingFactor) + averageFrequency * smoothingFactor;
        
        setLiveFrequency(smoothedFrequencyRef.current);
        
        // Transmettre à App.jsx pour l'animation du tore
        if (onLiveFrequencyChange) {
          onLiveFrequencyChange(smoothedFrequencyRef.current);
        }
      } else {
        // Diminuer progressivement la fréquence si pas de son détecté
        smoothedFrequencyRef.current *= 0.95;
        const finalFreq = smoothedFrequencyRef.current > 80 ? smoothedFrequencyRef.current : null;
        setLiveFrequency(finalFreq);
        
        // Transmettre à App.jsx
        if (onLiveFrequencyChange) {
          onLiveFrequencyChange(finalFreq);
        }
      }
      
      animationFrameRef.current = requestAnimationFrame(analyze);
    };
    
    analyze();
  };

  const analyzeRecording = async (audioBlob) => {
    try {
      console.log('🔬 analyzeRecording called with blob:', {
        size: audioBlob.size,
        type: audioBlob.type
      });
      
      if (audioBlob.size === 0) {
        console.error('❌ AudioBlob is empty!');
        return;
      }
      
      const stepId = getCurrentStepSafely().id;
      
      // Convert blob to base64 for persistent storage
      const base64Data = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(audioBlob);
      });
      
      // Create a persistent URL using base64
      const audioUrl = base64Data;
      
      // 🔥 UPLOAD IMMÉDIAT FIREBASE pour dataset
      const uploadToFirebase = async () => {
        try {
          if (!user?.uid) {
            console.log('❌ Pas d\'utilisateur connecté - skip Firebase');
            return;
          }

          // 🚫 PROTECTION LIMITE QUOTIDIENNE - 20 enregistrements/jour max
          const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
          const dailyCountKey = `firebase_daily_${user.uid}_${today}`;
          const currentCount = parseInt(localStorage.getItem(dailyCountKey) || '0');
          
          if (currentCount >= 20) {
            console.log('🚫 Limite quotidienne atteinte (20/20) - Skip Firebase');
            
            // Notifier l'utilisateur via spacebar hint
            if (onDiagnosticErrorChange) {
              onDiagnosticErrorChange('⚠️ Limite quotidienne atteinte (20 enregistrements/jour)');
              
              // Effacer le message après 5 secondes
              setTimeout(() => {
                onDiagnosticErrorChange('');
              }, 5000);
            }
            return;
          }

          // Trouver le nom du fichier correspondant à l'étape
          const step = STEPS.find(s => s.id === stepId);
          const fileName = step ? (typeof step.audioFile === 'function' ? step.audioFile() : step.audioFile) : `${stepId}-${Date.now()}.wav`;
          
          if (FIREBASE_UPLOAD_ENABLED) {
            // Dossier utilisateur dans Firebase Storage
            const userFolder = getUserFolderPath(user);
            const audioRef = ref(storage, `${userFolder}/${fileName}`);

            console.log(`🔥 Upload Firebase: ${fileName}...`);

            // Upload vers Firebase
            await uploadBytes(audioRef, audioBlob);

            console.log(`✅ Firebase: ${fileName} uploadé avec succès`);

            // ⬆️ INCRÉMENTER COMPTEUR QUOTIDIEN après upload réussi
            const newCount = currentCount + 1;
            localStorage.setItem(dailyCountKey, newCount.toString());
            setDailyUploads(newCount);
            console.log(`📊 Firebase uploads aujourd'hui: ${newCount}/20`);

            // Optionnel : sauvegarder métadonnées de l'étape
            const stepMetadata = {
              stepId,
              fileName,
              timestamp: new Date().toISOString(),
              size: audioBlob.size,
              userId: user.uid,
              stepNumber: currentStep + 1,
              stepName: step?.title || stepId
            };

            const metadataRef = ref(storage, `${userFolder}/${stepId}_metadata.json`);
            const metadataBlob = new Blob([JSON.stringify(stepMetadata, null, 2)], { type: 'application/json' });
            await uploadBytes(metadataRef, metadataBlob);

            console.log(`📝 Firebase: Métadonnées ${stepId} sauvées`);
          } else {
            console.log(`⏭️ Firebase upload skipped (disabled): ${fileName}`);
          }
          
        } catch (error) {
          console.error('❌ Erreur upload Firebase:', error);
          // Continue quand même - ne pas bloquer l'analyse IA
        }
      };

      // Lancer l'upload Firebase en parallèle (non-bloquant)
      uploadToFirebase();
      
      // Defer state updates to avoid render cycle conflicts
      setTimeout(() => {
        setRecordings(prev => ({
          ...prev,
          [stepId]: {
            url: audioUrl,              // Base64 data URL (persistent)
            blob: audioBlob,            // CONSERVER LE BLOB ORIGINAL
            timestamp: Date.now(),      // Pour cleanup
            size: audioBlob.size,       // Debug info
            isBase64: true,             // Flag to identify base64 URLs
            firebaseUploaded: true      // Flag Firebase upload
          }
        }));
        setIsRecording(false);
      }, 0);
      
      // 🔥 COOLDOWN + ANALYSE INDIVIDUELLE PROGRESSIVE
      if (isMinimal) {
        // Activer le cooldown immédiatement
        setIsAnalyzingBetweenSteps(true);
        
        // Notifier App.jsx du cooldown
        if (onAnalyzingBetweenStepsChange) {
          onAnalyzingBetweenStepsChange(true);
        }
        
        // Map des équations THOR 42 - NOUVEAU SYSTÈME
        const equationMessages = {
          'thor42': { equation: 'THOR42', archetype: '∆Φ-Complet' },
          // Anciens IDs pour compatibilité (au cas où)
          'identification': { equation: 'E1', archetype: '∆Φ-Racine' },
          'emotion-reading': { equation: 'E2', archetype: '∆Φ-Sacré' },
          'memory-dream': { equation: 'E3', archetype: '∆Φ-Plexus' },
          'vocalization': { equation: 'E4', archetype: '∆Φ-Cœur' },
          'conscious-breathing': { equation: 'E5', archetype: '∆Φ-Gorge' },
          'emotional-reading': { equation: 'E6', archetype: '∆Φ-Frontal' },
          'phoneme-repetition': { equation: 'E7', archetype: '∆Φ-Couronne' },
          'emotional-game': { equation: 'E8', archetype: '∆Φ-Éthérique' },
          'body-scan': { equation: 'E9', archetype: '∆Φ-Astral' },
          'cross-voice': { equation: 'E10', archetype: '∆Φ-Mental' },
          'free-voice': { equation: 'E11', archetype: '∆Φ-Causal' }
        };
        
        const equationInfo = equationMessages[stepId];
        
        // 🔍 DEBUG : Vérifier la correspondance stepId
        console.log('🔍 DEBUG stepId:', stepId, 'equationInfo:', equationInfo);
        
        // Lancer l'analyse IA individuelle
        const analysisPromise = (async () => {
          try {
            let analysis;
            try {
              analysis = await aiService.analyzeSingleRecording(audioBlob, user.uid);
            } catch (backendError) {
              console.log('Backend indisponible, fallback analyse locale:', backendError.message);
              analysis = await analyzeAudioLocally(audioBlob);
            }
            
            console.log('🔍 DEBUG analysis complet du backend:', analysis);
            
            const stepData = {
              // 🔥 GARDER TOUTE LA STRUCTURE pour ne rien perdre
              ...analysis,  // Tout l'objet analysis
              features: analysis.features || {},  // Référence explicite aux features
              timestamp: new Date().toISOString()
            };
            
            setStepAnalysis(prev => ({
              ...prev,
              [stepId]: stepData
            }));
            
            // Remplir le tore progressivement
            if (onDiagnosticProgress) {
              // 🎯 CORRECTION : Gérer le cas où equationInfo n'existe pas
              let equationIndex = 0; // Pour THOR42, on utilise toutes les équations d'un coup
              if (equationInfo && equationInfo.equation) {
                if (equationInfo.equation === 'THOR42') {
                  equationIndex = 0; // THOR42 remplit toutes les équations
                } else {
                  equationIndex = parseInt(equationInfo.equation.substring(1)) - 1; // E1 → 0, E2 → 1, etc.
                }
              } else {
                console.log('⚠️ equationInfo manquant, utilisation fallback index:', equationIndex);
              }
              
              // 🔧 NORMALISATION SCORE : S'assurer que le score est entre 0-1
              let normalizedScore = 0.7; // Score par défaut réaliste
              if (analysis.features?.thor_health_score) {
                const rawScore = analysis.features.thor_health_score;
                // Si c'est déjà un pourcentage (>1), le diviser par 100
                normalizedScore = rawScore > 1 ? rawScore / 100 : rawScore;
                // S'assurer qu'il reste dans les limites 0-1
                normalizedScore = Math.max(0, Math.min(1, normalizedScore));
              }
              
              // Log succinct pour suivi
              console.log(`✅ ${equationInfo.equation} complétée`);
              
              console.log('🔍 VoiceRecordingFlow received analysis:', {
                hasMetrics42: !!analysis.metrics_42,
                metricsCount: analysis.metrics_42 ? analysis.metrics_42.length : 0,
                hasEquationResults: !!analysis.equation_results,
                hasFeatures: !!analysis.features,
                hasPersonalized: !!analysis.personalized
              });
              
              onDiagnosticProgress(equationIndex, {
                message: analysis.personalized?.recommendation || `${equationInfo.equation} analysée`,
                score: normalizedScore,
                archetype: analysis.personalized?.frequency_archetype || equationInfo.archetype,
                emotion: analysis.personalized?.emotional_patterns || 'Neutre',
                // 🔥 NOUVEAU : Passer les vrais calculs d'équations
                equation_results: analysis.equation_results,
                features: analysis.features,
                personalized: analysis.personalized,
                metrics_42: analysis.metrics_42  // 🎵 42 metrics for profile display
              });
            }
            
            return analysis;
          } catch (error) {
            console.error("Erreur analyse IA:", error);
            return null;
          }
        })();
        
        // Attendre minimum 3s + fin analyse
        Promise.all([
          analysisPromise,
          new Promise(resolve => setTimeout(resolve, 3000))
        ]).then(() => {
          // Désactiver le cooldown
          setIsAnalyzingBetweenSteps(false);
          
          // Notifier App.jsx
          if (onAnalyzingBetweenStepsChange) {
            onAnalyzingBetweenStepsChange(false);
          }
          
          
          // Passer à l'étape suivante ou terminer
          if (currentStep < STEPS.length - 1) {
            setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
          } else {
            // Diagnostic THOR 42 complet - 42 équations analysées
            console.log('🎉 Diagnostic THOR 42 complet - 42 équations analysées !');
            
            // Forcer la fermeture du micro
            if (isRecording) {
              stopRecording();
            }
            
            // Notifier App.jsx que le diagnostic est complet
            if (onDiagnosticComplete) {
              // 🔥 UTILISER LES DONNÉES DE stepAnalysis car analysis n'est pas dans ce scope
              const thorData = stepAnalysis['thor42'] || {};
              
              console.log('🔍 DEBUG thorData depuis stepAnalysis:', thorData);
              console.log('🔍 DEBUG stepAnalysis complet:', stepAnalysis);
              
              const enrichedDiagnostic = {
                completed: true,
                stepAnalysis: stepAnalysis,
                // 🎯 EXTRAIRE LES VRAIES DONNÉES depuis stepAnalysis
                features: thorData.features || thorData || {},
                metrics: thorData.features || thorData || {},  // Les métriques sont dans features
                thor_health_score: thorData.thor_health_score || thorData.personalized?.thor42_analysis?.thor_health_score,
                ml_prediction: thorData.ml_prediction || thorData.personalized?.ml_prediction,
                thor_analysis: thorData.personalized?.thor42_analysis || thorData.thor42_analysis || {},
                equation_results: thorData.equation_results || {},
                // Données brutes complètes pour debug
                raw_analysis: thorData
              };
              
              console.log('🎯 VoiceRecordingFlow envoi diagnostic COMPLET:', enrichedDiagnostic);
              onDiagnosticComplete(enrichedDiagnostic);
            }
            
            // Afficher le message final avec lien profil
            setTimeout(() => {
              if (onDiagnosticErrorChange) {
                onDiagnosticErrorChange('✨ Diagnostic terminé ! Allez au [profil] pour voir les résultats');
              }
            }, 500); // Plus rapide pour voir le message
          }
        });
      }
      
      // Mode non-minimal (ancienne logique conservée)
      if (!isMinimal) {
        console.log('🧬 Starting personalized AI analysis for step:', stepId, 'User:', user.uid);
        setIsAnalyzing(true);
        try {
          const analysis = await aiService.analyzeSingleRecording(audioBlob, user.uid);
          const stepData = {
            ...analysis.features,
            personalized: analysis.personalized,
            equation_results: analysis.equation_results,  // 🔥 NOUVEAU: Équations réelles
            timestamp: new Date().toISOString()
          };
          
          setStepAnalysis(prev => ({
            ...prev,
            [stepId]: stepData
          }));
          
          // 🎯 REMPLIR LE TORE PROGRESSIVEMENT !
          if (onDiagnosticProgress) {
            const stepIndex = currentStep; // currentStep est 0-indexé (0-10)
            onDiagnosticProgress(stepIndex, {
              message: analysis.personalized?.recommendation || `Étape ${currentStep} analysée`,
              score: analysis.features?.thor_health_score || Math.random() * 100,
              archetype: analysis.personalized?.frequency_archetype || 'Non déterminé',
              emotion: analysis.personalized?.emotional_patterns || 'Neutre',
              equation_results: analysis.equation_results,
              features: analysis.features,
              personalized: analysis.personalized,
              metrics_42: analysis.metrics_42  // 🎵 42 metrics for profile display
            });
          }
        } catch (error) {
          console.error("Erreur analyse IA:", error);
          // Afficher un message d'erreur user-friendly
          setStepAnalysis(prev => ({
            ...prev,
            [stepId]: {
              error: true,
              message: "L'analyse n'a pas pu traiter cet enregistrement. Vous pouvez continuer.",
              timestamp: new Date().toISOString()
            }
          }));
        } finally {
          setIsAnalyzing(false);
        }
      }
      
    } catch (error) {
      console.error("Erreur lors de l'analyse de l'enregistrement:", error);
      setError("Erreur lors de l'analyse de l'enregistrement");
      // Notifier App.jsx de l'erreur
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange("Erreur lors de l'analyse de l'enregistrement");
      }
    }
  };

  const initializeAudio = async () => {
    try {
      if (!window.AudioContext && !window.webkitAudioContext) {
        throw new Error("Votre navigateur ne supporte pas l'API AudioContext");
      }

      // 🔍 S'assurer qu'on part toujours d'un état propre
      if (audioContextRef.current) {
        console.log("🧹 Fermeture de l'ancien AudioContext avant d'en créer un nouveau");
        try {
          await audioContextRef.current.close();
        } catch (e) {
          console.warn("Erreur fermeture ancien AudioContext:", e);
        }
        audioContextRef.current = null;
      }

      // Créer un NOUVEAU AudioContext propre à chaque fois
      console.log("🆕 Création d'un AudioContext frais pour le diagnostic");
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048;
      analyserRef.current.smoothingTimeConstant = 0.8;
      analyserRef.current.minDecibels = -90;
      analyserRef.current.maxDecibels = -10;

      console.log("✅ Audio context et analyser initialisés avec succès - État:", audioContextRef.current.state);
      return true;
    } catch (error) {
      console.error("Erreur lors de l'initialisation audio:", error);
      const errorMsg = "Impossible d'initialiser l'audio. Veuillez vérifier votre navigateur.";
      setError(errorMsg);
      // Notifier App.jsx de l'erreur
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange(errorMsg);
      }
      return false;
    }
  };

  const startRecording = async () => {
    try {
      setError(null);
      // Nettoyer l'erreur dans App.jsx aussi
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange('');
      }
      
      // 🧹 NETTOYAGE PRÉVENTIF : S'assurer qu'aucun stream ne traîne
      if (mediaStreamRef.current) {
        console.log("🧹 NETTOYAGE: Fermeture d'un ancien stream qui traînait");
        mediaStreamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log("🔴 Ancien track fermé:", track.kind);
        });
        mediaStreamRef.current = null;
      }
      
      setIsRecording(true);
      setIsPaused(false);
      
      // Notifier App.jsx de l'état d'enregistrement
      if (onRecordingStateChange) {
        onRecordingStateChange(true);
      }

      const audioInitialized = await initializeAudio();
      if (!audioInitialized) {
        setIsRecording(false);
        return;
      }

      // Créer le MediaStream UNE SEULE FOIS ici
      const stream = await requestMicrophone(defaultAudioConstraints);
      mediaStreamRef.current = stream;
      console.log("📡 MediaStream créé:", stream.getTracks().length, "tracks");

      // Connecter le stream à l'analyseur
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 128000
      });

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        console.log('🎤 Chunks collected:', audioChunksRef.current.length);
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        console.log('🎵 AudioBlob created:', {
          size: audioBlob.size,
          type: audioBlob.type
        });
        
        // 🔑 DÉLAI CRUCIAL : Laisser le temps au micro de se fermer complètement
        console.log('⏳ Attente 1 seconde pour fermeture complète du micro...');
        console.log('🔍 État MediaStream avant délai:', mediaStreamRef.current ? 'EXISTE ENCORE' : 'NULL');
        await new Promise(resolve => setTimeout(resolve, 1000));
        console.log('✅ Délai écoulé, lancement de l\'analyse Docthor');
        console.log('🔍 État MediaStream après délai:', mediaStreamRef.current ? 'EXISTE TOUJOURS!' : 'NULL');
        
        await analyzeRecording(audioBlob);
      };

      audioChunksRef.current = [];
      mediaRecorderRef.current.start();
      startLiveAnalysis();
      
      // Démarrer le timer avec arrêt automatique
      const duration = getCurrentStepSafely().duration || 30;
      setCountdown(duration);
      
      // Notifier immédiatement App.jsx du countdown initial
      if (onCountdownChange) {
        onCountdownChange(duration);
      }
      
      timerRef.current = setInterval(() => {
        setCountdown(prev => {
          const newValue = prev <= 1 ? 0 : prev - 1;
          
          // Mettre à jour l'instruction selon la phase THOR 42
          const newInstruction = getThor42Instruction(newValue);
          setCurrentInstruction(newInstruction);
          
          // Notifier App.jsx du countdown (defer to avoid render cycle)
          if (onCountdownChange) {
            setTimeout(() => onCountdownChange(newValue), 0);
          }
          
          if (newValue <= 0) {
            clearInterval(timerRef.current);
            
            // 🔒 FORCER FERMETURE MICRO IMMÉDIATE AVANT TOUT
            if (mediaStreamRef.current) {
              console.log("🔒 FERMETURE FORCÉE IMMÉDIATE du micro");
              mediaStreamRef.current.getTracks().forEach(track => {
                track.stop();
                console.log("🔴 Track fermée immédiatement:", track.kind);
              });
            }
            
            // ⏳ DÉLAI AVANT stopRecording pour laisser le temps au micro de se fermer
            setTimeout(() => {
              console.log("⏳ Timer expiré - Délai écoulé, arrêt final");
              stopRecording();
            }, 500);
          }
          
          return newValue;
        });
      }, 1000);

    } catch (error) {
      console.error("Erreur lors du démarrage de l'enregistrement:", error);
      const friendly = getFriendlyMicError(error);
      setError(friendly);
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange(friendly);
      }
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    try {
      console.log("🛑 ARRÊT SIMPLE et IMMÉDIAT");
      
      // 1. FERMER LE MICRO EN PREMIER (priorité absolue)
      if (mediaStreamRef.current) {
        console.log("🔒 FERMETURE FORCÉE des tracks");
        mediaStreamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log("🔴 Track fermée:", track.kind, track.readyState);
        });
        mediaStreamRef.current = null;
      }
      
      // 2. Arrêter le timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setCountdown(0);
      
      // 3. Arrêter l'animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // 4. Arrêter MediaRecorder (normal) 
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        console.log("🎬 Arrêt MediaRecorder");
        mediaRecorderRef.current.stop();
        
        // ⏳ ATTENDRE que le micro soit vraiment fermé avant que l'IA démarre
        console.log("⏳ Attente pour fermeture complète du micro...");
      }

      // 5. Fermer AudioContext
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        console.log("🔊 Fermeture AudioContext");
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      // 6. États
      setIsRecording(false);
      setLiveFrequency(null);
      
      // 7. Notifier App.jsx
      if (onRecordingStateChange) {
        onRecordingStateChange(false);
      }
      if (onCountdownChange) {
        onCountdownChange(0);
      }
      if (onLiveFrequencyChange) {
        onLiveFrequencyChange(null);
      }

      console.log("✅ MICRO FERMÉ - Arrêt terminé");

    } catch (error) {
      console.error("Erreur lors de l'arrêt:", error);
      setIsRecording(false);
    }
  };


  // Exposer les fonctions d'enregistrement pour App.jsx (bouton micro)
  useEffect(() => {
    if (onStartRecording) {
      onStartRecording(startRecording);
    }
    if (onStopRecording) {
      onStopRecording(stopRecording);
    }
  }, [onStartRecording, onStopRecording]);

  // Hook global pour barre d'espace 
  const canRecord = !showConfirmExit && !showRecap && !showSuccess && !showFinalDiagnostic && !recordings[getCurrentStepSafely().id]?.blob && !isAnalyzingBetweenSteps;
  useSpacebarRecording(isRecording, canRecord && !isMinimal, startRecording, stopRecording);
  
  // Effet pour gérer la barre d'espace en mode minimal (doit être AVANT les returns)
  useEffect(() => {
    if (!isMinimal) return;
    
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        
        // Empêcher si on est dans un état non-enregistrable ou pendant le cooldown
        if (showConfirmExit || showRecap || showSuccess || showFinalDiagnostic || isAnalyzingBetweenSteps) {
          return;
        }
        
        console.log('💯 MINIMAL MODE KeyDown detected!');
        if (!isRecording) {
          console.log('🎤 START via spacebar (minimal mode)');
          startRecording();
        } else {
          console.log('🛑 STOP via spacebar (minimal mode)');
          stopRecording();
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMinimal, isRecording, showConfirmExit, showRecap, showSuccess, showFinalDiagnostic, currentStep]);

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
    }
  };

  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Dernière étape atteinte - proposer de voir le diagnostic final
      onClose('show-ai-diagnostic');
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleBack = () => {
    if (isRecording) {
      setError('Veuillez arrêter l\'enregistrement en cours avant de quitter');
      return;
    }
    setShowConfirmExit(true);
  };

  const handleConfirmExit = () => {
    // Nettoyer les ressources audio
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    onClose();
  };

  const handleCancelExit = () => {
    setShowConfirmExit(false);
  };

  // Fonction utilitaire pour slugifier les champs (nom, prénom, email)
  function slugify(str) {
    return String(str)
      .normalize('NFD')
      .replace(/[^\w\s.-@]/g, '')
      .replace(/\s+/g, '_')
      .replace(/@/g, '_at_')
      .replace(/\./g, '_')
      .toLowerCase();
  }


  // Fonction pour uploader les enregistrements vers Firebase
  const submitRecordings = async () => {
    try {
      setIsSubmitting(true);
      setError('');
      // Notifier App.jsx du loading
      if (onDiagnosticLoadingChange) {
        onDiagnosticLoadingChange(true);
      }
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange('');
      }
      
      console.log('🚀 Début de l\'upload des enregistrements...');
      
      // Préparer les informations utilisateur
      const userInfo = {
        nom: user.displayName?.split(' ')[1] || 'Non renseigné',
        prenom: user.displayName?.split(' ')[0] || 'Non renseigné', 
        email: user.email || 'Non renseigné',
        timestamp: new Date().toISOString()
      };
      
      // Dossier utilisateur dans Firebase Storage
      const userFolder = `recordings/${user.uid}`;
      
      // Convertir chaque enregistrement et l'uploader
      const uploadPromises = Object.entries(recordings).map(async ([stepId, recordingData]) => {
        try {
          // Debug info pour diagnostiquer le problème
          console.log(`🔍 Processing ${stepId}:`, {
            hasBlob: !!recordingData.blob,
            hasUrl: !!recordingData.url,
            blobSize: recordingData.blob?.size,
            dataType: typeof recordingData
          });
          
          // Récupérer le blob depuis l'enregistrement ou recréer depuis base64
          let audioBlob = recordingData.blob;
          
          if (!audioBlob && recordingData.url && recordingData.isBase64) {
            // Convertir base64 data URL vers blob
            const response = await fetch(recordingData.url);
            audioBlob = await response.blob();
            console.log(`🔄 Blob récupéré depuis base64 pour ${stepId}: ${audioBlob.size} bytes`);
          }
          
          if (!audioBlob) {
            // Fallback: essayer de récupérer depuis l'URL (ancien format)
            const url = recordingData.url || recordingData;
            if (typeof url === 'string') {
              audioBlob = await fetch(url).then(r => r.blob());
            }
          }
          
          if (!audioBlob || audioBlob.size === 0) {
            throw new Error(`Blob invalide pour ${stepId}: taille ${audioBlob?.size || 0}`);
          }
          
          // Trouver le nom du fichier correspondant à l'étape
          const step = STEPS.find(s => s.id === stepId);
          const fileName = step ? step.audioFile : `${stepId}.webm`;
          
          if (FIREBASE_UPLOAD_ENABLED) {
            // Référence Firebase Storage
            const audioRef = ref(storage, `${userFolder}/${fileName}`);

            console.log(`📤 Upload de ${fileName}...`);

            // Upload vers Firebase
            await uploadBytes(audioRef, audioBlob);

            // Récupérer l'URL de téléchargement
            const downloadURL = await getDownloadURL(audioRef);

            console.log(`✅ ${fileName} uploadé avec succès`);

            return {
              stepId,
              fileName,
              url: downloadURL,
              size: audioBlob.size
            };
          } else {
            console.log(`⏭️ Firebase upload skipped (disabled): ${fileName}`);
            return {
              stepId,
              fileName,
              url: null,
              size: audioBlob.size
            };
          }
          
        } catch (error) {
          console.error(`❌ Erreur upload ${stepId}:`, error);
          throw new Error(`Erreur lors de l'upload de ${stepId}`);
        }
      });
      
      // Attendre tous les uploads
      const uploadResults = await Promise.all(uploadPromises);
      
      // Créer un fichier métadonnées
      const metadata = {
        userInfo,
        recordings: uploadResults,
        diagnostic: {
          completed_at: new Date().toISOString(),
          total_steps: STEPS.length,
          analysis: stepAnalysis
        }
      };
      
      if (FIREBASE_UPLOAD_ENABLED) {
        // Upload des métadonnées
        const metadataRef = ref(storage, `${userFolder}/metadata.json`);
        const metadataBlob = new Blob([JSON.stringify(metadata, null, 2)], { type: 'application/json' });
        await uploadBytes(metadataRef, metadataBlob);

        console.log('✅ Tous les enregistrements uploadés avec succès!');
      } else {
        console.log('⏭️ Firebase metadata upload skipped (disabled)');
      }
      
      // Lancer l'analyse finale complète avec Docthor
      await performFinalAnalysis();
      
      // Ne pas montrer le succès immédiatement - attendre le diagnostic
      setShowRecap(false);
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload:', error);
      const errorMsg = `Erreur lors de l'envoi: ${error.message}`;
      setError(errorMsg);
      // Notifier App.jsx de l'erreur
      if (onDiagnosticErrorChange) {
        onDiagnosticErrorChange(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
      // Arrêter le loading dans App.jsx
      if (onDiagnosticLoadingChange) {
        onDiagnosticLoadingChange(false);
      }
    }
  };


  if (showConfirmExit) {
    return (
      <animated.div style={spring} className="voice-recording-flow">
        <div className="step-container">
          <div className="step-header">
            <h2>Confirmation</h2>
          </div>
          <div className="instruction-box">
            <p>Voulez-vous vraiment quitter ? Votre progression sera sauvegardée.</p>
            <div className="navigation-buttons">
              <button onClick={handleCancelExit} className="nav-button prev">
                Continuer
              </button>
              <button onClick={handleConfirmExit} className="nav-button next">
                Quitter
              </button>
            </div>
          </div>
        </div>
      </animated.div>
    );
  }

  // Page userInfo supprimée - utilisation directe des données Firebase



  // Plus de return early - on continue normalement même avec warning
  // App.jsx gère maintenant l'affichage du message


  const isLastStep = currentStep === STEPS.length - 1;


  // Fonction pour passer à l'étape suivante
  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(Math.min(currentStep + 1, STEPS.length - 1));
    } else {
      // Fin du diagnostic
      onClose();
    }
  };


  // Mode minimal - UTILISÉ en production (AuthenticatedApp.jsx ligne 2159)
  // Interface ultra-simple qui affiche juste la question au-dessus du tore
  if (isMinimal) {
    // ✨ SUPPRESSION TOTALE PAGE FALLBACK - fermeture immédiate en mode minimal ✨

    // Interface de chargement simple
    if (finalAnalyzing) {
      return (
        <div className="diagnostic-minimal">
          <div className="diagnostic-question-minimal">
            <p>🧠 Analyse de vos enregistrements...</p>
            <div className="loading-dots-elegant">
              <span className="dot">.</span>
              <span className="dot">.</span>
              <span className="dot">.</span>
            </div>
            <p style={{fontSize: '0.9rem', opacity: 0.8, marginTop: '1rem'}}>
              Analyse IA en cours...
            </p>
          </div>
        </div>
      );
    }

    // Interface de succès
    if (showSuccess) {
      return (
        <div className="diagnostic-minimal">
          <div className="diagnostic-success">
            <h3>✅ Diagnostic terminé !</h3>
            <p>Vos enregistrements ont été envoyés avec succès.</p>
            <button onClick={onClose} className="close-success">Fermer</button>
          </div>
        </div>
      );
    }

    // ✨ POPUP IMMONDE SUPPRIMÉ ! Flux automatique élégant maintenant ✨

    // Interface normale
    return (
      <div className="diagnostic-minimal">
        <div className="diagnostic-question-minimal">
          <p>{isRecording ? currentInstruction : getCurrentStepSafely().instruction}</p>
        </div>
      </div>
    );
  }

  // Mode overlay - NON UTILISÉ actuellement
  // Interface overlay avec header, bouton fermer, contrôles basiques
  if (isOverlay) {
    return (
      <div className="diagnostic-overlay-content">
        <div className="diagnostic-header">
          <h3>{getCurrentStepSafely().title}</h3>
          <button onClick={onClose} className="close-overlay">×</button>
        </div>
        
        <div className="diagnostic-question">
          <p>{isRecording ? currentInstruction : getCurrentStepSafely().instruction}</p>
          <div className="diagnostic-controls">
            <div className="spacebar-hint">
              <div className={`mic-indicator ${isRecording ? 'active' : ''}`}></div>
              <span>Barre d'espace pour parler</span>
            </div>
            {countdown > 0 && isRecording && (
              <div className="countdown-timer">{countdown}s</div>
            )}
          </div>
        </div>

        {/* Navigation simple */}
        <div className="diagnostic-navigation">
          <span>Étape {currentStep + 1}/{STEPS.length}</span>
          {recordings[getCurrentStepSafely().id]?.url && (
            <button onClick={handleNext} className="next-step">Suivant →</button>
          )}
        </div>
      </div>
    );
  }

  // Mode complet - NON UTILISÉ actuellement
  // Interface page complète avec bouton retour, analyse détaillée, timer flottant
  return (
    <animated.div style={spring} className="voice-recording-flow">
      <div className="step-container">
        <div className="step-header">
          <button onClick={handleBack} className="back-button">
            ← Retour
          </button>
          <h2>{getCurrentStepSafely().title}</h2>
          <p className="duration">Durée suggérée : {getCurrentStepSafely().duration}s</p>
        </div>

        <div className="instruction-box">
          <p>{isRecording ? currentInstruction : getCurrentStepSafely().instruction}</p>
          <div className="spacebar-hint">
            <div className={`mic-indicator ${isRecording ? 'active' : ''}`}></div>
            <span>Barre d'espace pour parler</span>
          </div>
          
          {/* Affichage de l'analyse en temps réel */}
          {isAnalyzing && (
            <div className="analysis-loading">
              <p>🩺 Analyse de vos fréquences vocales...</p>
              <div className="loading-dots">
                <span>•</span><span>•</span><span>•</span>
              </div>
            </div>
          )}
          
          {stepAnalysis[getCurrentStepSafely().id] && (
            <div className="step-analysis-feedback">
              {stepAnalysis[getCurrentStepSafely().id].error ? (
                <div className="analysis-error">
                  <h4>🤔 Analyse</h4>
                  <p>{stepAnalysis[getCurrentStepSafely().id].message}</p>
                </div>
              ) : (
                <>
                  <h4>📊 Analyse vocale</h4>
                  <div className="analysis-metrics">
                    {stepAnalysis[getCurrentStepSafely().id].f0_mean && (
                      <div className="metric">
                        <span className="label">Fréquence fondamentale:</span>
                        <span className="value">{Math.round(stepAnalysis[getCurrentStepSafely().id].f0_mean)} Hz</span>
                      </div>
                    )}
                    {stepAnalysis[getCurrentStepSafely().id].energy_mean !== undefined && (
                      <div className="metric">
                        <span className="label">Énergie vocale:</span>
                        <span className="value">{Math.round(stepAnalysis[getCurrentStepSafely().id].energy_mean * 100)}%</span>
                      </div>
                    )}
                    {stepAnalysis[getCurrentStepSafely().id].jitter !== undefined && (
                      <div className="metric">
                        <span className="label">Stabilité (jitter):</span>
                        <span className="value">{stepAnalysis[getCurrentStepSafely().id].jitter.toFixed(2)}%</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="recording-controls" style={{ minHeight: '400px', position: 'relative' }}>
          {/* Timer flottant */}
          {isRecording && countdown > 0 && (
            <div className="floating-timer">
              {countdown}s
            </div>
          )}
          
          <TorusAnimation
            liveFrequency={liveFrequency}
            isGuidedRecording={isRecording}
            isRecording={isRecording}
          >
            {(!isRecording && !recordings[getCurrentStepSafely().id]?.blob) && (
              <button
                className="floating-mic-button"
                onClick={startRecording}
                aria-label="Démarrer l'enregistrement"
              >
                Je parle
              </button>
            )}
            {isRecording && (
              <button
                className="floating-mic-button recording"
                onClick={stopRecording}
                aria-label="Arrêter l'enregistrement"
              >
                J'arrête
              </button>
            )}
          </TorusAnimation>
        </div>


        {error && (
          <div className="error-message" style={{ color: 'red', marginTop: '1rem', textAlign: 'center' }}>
            {error}
          </div>
        )}

        {loading && (
          <div className="loading-message" style={{ marginTop: '1rem', textAlign: 'center' }}>
            Envoi en cours...
          </div>
        )}
      </div>
    </animated.div>
  );
};

export default VoiceRecordingFlow;
